import { createProblemReport } from '../../api/generated/problem-reports/problem-reports'
import type { ProblemReportCreate } from '../../api/generated/model/problemReportCreate'
import { reportHostState, reportRequestContext, settleReportRequest, subscribeReportHost } from '../../lib/problem-reporting/context'
import type { ReportRequestContext } from '../../lib/problem-reporting/context'
import { problemReportMetadata } from '../../lib/problem-reporting/metadata'
import { problemReportSnapshot } from '../../lib/problem-reporting/runtime'
import { flattenRedactions, normalizeReportImage } from '../../lib/problem-reporting/images'
import type { ImageRectangle } from '../../lib/problem-reporting/images'
import { releaseImage, uploadReportImage } from './attachments'
import type { ImageWork, SubmissionImage } from './attachments'

export interface SubmissionError { code: string; status?: number; retryAfter?: number }
export interface ReportDraft { kind: ProblemReportCreate['kind']; description: string; impact?: ProblemReportCreate['impact'] }
export interface SubmissionState {
  draft: Readonly<ReportDraft>
  outcome: 'draft' | 'pending' | 'unknown' | 'rejected' | 'saved'
  report: Readonly<{ id: string; reference: string }> | null
  error: Readonly<SubmissionError> | null
  images: readonly Readonly<SubmissionImage>[]
  preparing: boolean
  summary: Readonly<{ attached: number; failed: number; pending: number }>
}
export function submissionError(error: unknown): SubmissionError {
  const value = error as { status?: unknown; problem?: { code?: unknown }; retryAfter?: unknown } | null
  const status = typeof value?.status === 'number' ? value.status : undefined
  const code = typeof value?.problem?.code === 'string' && /^[a-z_]{1,80}$/.test(value.problem.code)
    ? value.problem.code : 'request_failed'
  const retryAfter = typeof value?.retryAfter === 'string' && /^\d{1,10}$/.test(value.retryAfter) ? Number(value.retryAfter) : undefined
  return { code, ...(status ? { status } : {}), ...(retryAfter !== undefined ? { retryAfter } : {}) }
}
function initialState(): SubmissionState {
  return Object.freeze({ draft: Object.freeze({ kind: 'bug', description: '' }), outcome: 'draft', report: null, error: null,
    images: Object.freeze([]), preparing: false, summary: Object.freeze({ attached: 0, failed: 0, pending: 0 }) })
}
export function createReportSubmission() {
  let state = initialState()
  let generation = reportHostState().generation
  let epoch = 0
  let disposed = false
  let frozen: { body: ProblemReportCreate; context: ReportRequestContext; uncertain: boolean } | null = null
  let sending: Promise<void> | null = null
  const requests = new Set<AbortController>()
  const listeners = new Set<() => void>()
  let images: ImageWork[] = []
  const publish = (patch: Partial<SubmissionState>) => {
    if (disposed) return
    state = Object.freeze({ ...state, ...patch })
    for (const listener of listeners) listener()
  }
  function reset() {
    epoch += 1
    for (const request of requests) request.abort()
    requests.clear(); frozen = null; sending = null
    for (const image of images) releaseImage(image)
    images = []
    state = initialState()
  }
  const unsubscribe = subscribeReportHost(() => {
    if (generation === reportHostState().generation) return
    generation = reportHostState().generation; reset(); publish({})
  })
  const editable = () => !disposed && !frozen && state.outcome !== 'saved'
  const publishImages = () => {
    const attached = images.filter(image => image.view.status === 'attached').length
    const failed = images.filter(image => image.view.status === 'failed').length
    publish({ images: Object.freeze(images.map(image => Object.freeze({ ...image.view }))),
      summary: Object.freeze({ attached, failed, pending: images.length - attached - failed }) })
  }
  async function prepare(blob: Blob, replacement?: ImageWork, rectangles?: readonly ImageRectangle[]) {
    if (!editable() || state.preparing || (!replacement && images.length >= 3)) return null
    const ownEpoch = epoch
    let context: ReportRequestContext
    try { context = reportRequestContext() } catch { return null }
    const request = new AbortController(); requests.add(request)
    publish({ preparing: true, error: null })
    try {
      const normalized = rectangles ? await flattenRedactions(blob, rectangles, { signal: request.signal })
        : await normalizeReportImage(blob, { signal: request.signal })
      const digest = await crypto.subtle.digest('SHA-256', await normalized.arrayBuffer())
      await settleReportRequest(context, request.signal)
      if (disposed || ownEpoch !== epoch) return null
      const key = replacement?.view.key ?? crypto.randomUUID()
      const image: ImageWork = {
        view: { key, kind: 'image', previewUrl: URL.createObjectURL(normalized), status: 'staged', confirmation: 'not_attempted', error: null, attachment: null },
        blob: normalized, grant: null, running: false,
        metadata: { attachment_key: key, original_sha256: [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join(''),
          mime_type: 'image/png', byte_length: normalized.size, filename: replacement?.metadata.filename ?? `image-${images.length + 1}.png` },
      }
      if (replacement) { images = images.map(item => item === replacement ? image : item); releaseImage(replacement) }
      else images.push(image)
      publishImages(); return key
    } catch {
      if (!disposed && ownEpoch === epoch) publish({ error: Object.freeze({ code: 'image_preparation_failed' }) })
      return null
    } finally {
      requests.delete(request)
      if (!disposed && ownEpoch === epoch) publish({ preparing: false })
    }
  }
  async function prepareVideo(blob: Blob) {
    if (!editable() || state.preparing || images.length >= 3) return null
    const ownEpoch = epoch
    let context: ReportRequestContext
    try { context = reportRequestContext() } catch { return null }
    const request = new AbortController(); requests.add(request)
    publish({ preparing: true, error: null })
    try {
      if (!['video/webm', 'video/mp4'].includes(blob.type) || !blob.size || blob.size > 20_971_520) {
        throw new Error('Invalid recording')
      }
      const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
      await settleReportRequest(context, request.signal)
      if (disposed || ownEpoch !== epoch) return null
      const key = crypto.randomUUID()
      const work: ImageWork = {
        view: { key, kind: 'video', previewUrl: URL.createObjectURL(blob), status: 'staged', confirmation: 'not_attempted', error: null, attachment: null },
        blob, grant: null, running: false,
        metadata: { attachment_key: key, original_sha256: [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join(''),
          mime_type: blob.type as 'video/webm' | 'video/mp4', byte_length: blob.size,
          filename: blob.type === 'video/webm' ? 'screen-recording.webm' : 'screen-recording.mp4' },
      }
      images.push(work); publishImages(); return key
    } catch {
      if (!disposed && ownEpoch === epoch) publish({ error: Object.freeze({ code: 'video_preparation_failed' }) })
      return null
    } finally {
      requests.delete(request)
      if (!disposed && ownEpoch === epoch) publish({ preparing: false })
    }
  }
  async function retryImage(key: string) {
    const image = images.find(item => item.view.key === key)
    if (disposed || !frozen || !state.report || !image || image.running || image.view.status === 'attached' || image.view.confirmation === 'rejected') return
    const ownEpoch = epoch; const attempt = frozen; const reportId = state.report.id
    const request = new AbortController(); requests.add(request); image.running = true
    try {
      await uploadReportImage(image, reportId, attempt.context, request.signal, () => {
        if (!disposed && ownEpoch === epoch) publishImages()
      })
    } catch (error) {
      if (disposed || ownEpoch !== epoch) return
      image.view = { ...image.view, status: 'failed', error: Object.freeze(submissionError(error)) }; publishImages()
    } finally { image.running = false; requests.delete(request) }
  }
  async function sendCreate(routeId: string) {
    const ownEpoch = epoch
    const request = new AbortController()
    requests.add(request)
    try {
      if (!frozen) {
        const host = reportHostState()
        if (host.phase !== 'ready' || !host.identity || !host.policy?.enabled || !host.policy.can_self) {
          publish({ error: Object.freeze({ code: 'reporting_unavailable' }) }); return
        }
        const description = state.draft.description.trim()
        if ([...description].length < 10 || [...description].length > 5000) {
          publish({ error: Object.freeze({ code: 'invalid_description' }) }); return
        }
        if (!['bug', 'improvement'].includes(state.draft.kind) ||
          (state.draft.impact != null && !['blocking', 'difficult', 'minor'].includes(state.draft.impact))) {
          publish({ error: Object.freeze({ code: 'invalid_draft' }) }); return
        }
        const metadata = problemReportMetadata(routeId)
        frozen = { context: reportRequestContext(), uncertain: false, body: {
          create_key: crypto.randomUUID(), kind: state.draft.kind, description,
          ...(state.draft.impact != null ? { impact: state.draft.impact } : {}),
          ...metadata, context: { ...metadata.context, ...problemReportSnapshot() },
        } }
      }
      const attempt = frozen
      publish({ outcome: 'pending', error: null })
      await settleReportRequest(attempt.context, request.signal)
      const response = await createProblemReport(attempt.body, {
        signal: request.signal, headers: { 'X-Problem-Report-Context': attempt.context.identity.membershipId },
      })
      await settleReportRequest(attempt.context, request.signal)
      if (disposed || ownEpoch !== epoch) return
      if (response.status !== 200 && response.status !== 201) throw { status: response.status, problem: response.data }
      publish({ outcome: 'saved', report: Object.freeze({ id: response.data.id, reference: response.data.reference }), error: null })
      for (const image of images) {
        if (disposed || ownEpoch !== epoch) return
        await retryImage(image.view.key)
      }
    } catch (error) {
      if (disposed || ownEpoch !== epoch) return
      const safe = submissionError(error)
      // A later pre-handler rejection cannot resolve a previous lost receipt.
      const rejected = !frozen?.uncertain && safe.status !== undefined && safe.status >= 400 && safe.status < 500 && safe.status !== 408 && safe.code !== 'report_create_key_conflict'
      if (rejected) frozen = null
      else if (frozen) frozen.uncertain = true
      publish({ outcome: rejected ? 'rejected' : 'unknown', error: Object.freeze(safe) })
    } finally { requests.delete(request) }
  }
  return {
    getState: () => state,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener) } },
    updateDraft(patch: Partial<ReportDraft>) {
      if (!editable()) return false
      publish({ draft: Object.freeze({ ...state.draft, ...patch }), error: null }); return true
    },
    addImage: (blob: Blob) => prepare(blob),
    addVideo: prepareVideo,
    async replaceImage(key: string, blob: Blob) {
      const image = images.find(item => item.view.key === key)
      return image ? (await prepare(blob, image)) !== null : false
    },
    async redactImage(key: string, rectangles: readonly ImageRectangle[]) {
      const image = images.find(item => item.view.key === key)
      return image?.blob ? (await prepare(image.blob, image, rectangles)) !== null : false
    },
    removeImage(key: string) {
      if (!editable() || state.preparing) return false
      const image = images.find(item => item.view.key === key)
      if (!image) return false
      releaseImage(image); images = images.filter(item => item !== image); publishImages(); return true
    },
    retryImage,
    send(routeId: string): Promise<void> {
      if (disposed || state.preparing || state.outcome === 'saved') return Promise.resolve()
      if (sending) return sending
      const ownEpoch = epoch
      sending = sendCreate(routeId).finally(() => { if (epoch === ownEpoch) sending = null })
      return sending
    },
    requestClose() {
      const savedAndSettled = state.outcome === 'saved' && state.report && !state.preparing &&
        images.every(image => !image.running && image.view.status === 'attached' && image.view.confirmation === 'confirmed')
      if (savedAndSettled) return 'close' as const
      const unfinished = state.draft.description || state.draft.kind !== 'bug' || state.draft.impact != null ||
        images.length || state.preparing || state.outcome === 'pending' || state.outcome === 'unknown'
      return unfinished ? 'confirm-discard' as const : 'close' as const
    },
    discard() {
      // A lost confirmation may already be durable; only its original tuple can recover it.
      if (disposed || state.outcome === 'pending' || state.outcome === 'unknown' || images.some(image => image.running || image.view.confirmation === 'unresolved')) return
      reset(); publish({})
    },
    dispose() { if (disposed) return; disposed = true; unsubscribe(); reset(); listeners.clear() },
  }
}
